import React, { useEffect, useState } from 'react';
import { FaMoon, FaSun } from 'react-icons/fa';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;

export default function Profile() {
  const [dark, setDark] = useState(true);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);

  const [selectedProvinceId, setSelectedProvinceId] = useState(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    birthDateShamsi: '',  // "YYYY-MM-DD" شمسی
    cityId: null,
    addressLine: '',
    postalCode: ''
  });

  // برای کنترل DatePicker
  const [birthDatePickerValue, setBirthDatePickerValue] = useState(null);

  // دریافت پروفایل + استان‌ها
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [profileRes, provRes] = await Promise.all([
          fetch(`https://api.mogym.ir/api/profile`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }),
          fetch(`https://api.mogym.ir/api/location/provinces`)
        ]);

        if (!profileRes.ok) throw new Error('خطا در دریافت پروفایل');
        if (!provRes.ok) throw new Error('خطا در دریافت استان‌ها');

        const profile = await profileRes.json();
        const provs = await provRes.json();

        setProvinces(provs);

        // اگر cityId داشت، شهرهای استان مرتبط
        if (profile.CityId && profile.ProvinceId) {
          setSelectedProvinceId(profile.ProvinceId);

          const cityRes = await fetch(
            `https://api.mogym.ir/api/location/cities/${profile.ProvinceId}`
          );
          if (cityRes.ok) {
            const cityData = await cityRes.json();
            setCities(cityData);
          }
        }

        setForm({
          firstName: profile.FirstName || '',
          lastName: profile.LastName || '',
          birthDateShamsi: profile.BirthDate || '',
          cityId: profile.CityId || null,
          addressLine: profile.AddressLine || '',
          postalCode: profile.PostalCode || ''
        });

        // مقداردهی اولیه DatePicker اگر تاریخ تولد داشتیم
        if (profile.BirthDate) {
          try {
            const d = new DateObject({
              date: profile.BirthDate,
              format: 'YYYY-MM-DD',
              calendar: persian,
              locale: persian_fa
            });
            setBirthDatePickerValue(d);
          } catch {
            setBirthDatePickerValue(null);
          }
        }
      } catch (err) {
        console.error(err);
        setError('مشکلی در دریافت اطلاعات رخ داد.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleProvinceChange = async (e) => {
    const provId = e.target.value ? parseInt(e.target.value, 10) : null;
    setSelectedProvinceId(provId);
    setForm((prev) => ({ ...prev, cityId: null }));

    if (!provId) {
      setCities([]);
      return;
    }

    try {
      const res = await fetch(`https://api.mogym.ir/api/location/cities/${provId}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCities(data);
    } catch {
      setError('دریافت لیست شهرها ناموفق بود.');
    }
  };

  const handleCityChange = (e) => {
    const cityId = e.target.value ? parseInt(e.target.value, 10) : null;
    setForm((prev) => ({ ...prev, cityId }));
  };

  // وقتی تاریخ در DatePicker عوض شد
  const handleBirthDateChange = (value) => {
    setBirthDatePickerValue(value);

    if (!value) {
      setForm((prev) => ({ ...prev, birthDateShamsi: '' }));
      return;
    }

    // value یک DateObject است → خروجی به فرمت YYYY-MM-DD شمسی
    const formatted = value.format('YYYY-MM-DD');
    setForm((prev) => ({ ...prev, birthDateShamsi: formatted }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const res = await fetch(`https://api.mogym.ir/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(form)
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'خطا در ذخیره پروفایل');
      }
      alert('پروفایل با موفقیت ذخیره شد.');
      navigate('/Dashboard')
    } catch (err) {
      console.error(err);
      setError('خطا در ذخیره پروفایل. لطفاً مقادیر را بررسی کنید.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={dark ? 'dark' : ''}>
        <div className="min-h-screen bg-white dark:bg-[#0e1015] text-black dark:text-white flex items-center justify-center">
          <p>در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={dark ? 'dark' : ''}>
      <div className="min-h-screen bg-white dark:bg-[#0e1015] text-black dark:text-white px-4 py-8">
        <div className="max-w-xl mx-auto p-4 space-y-4 bg-gray-50 dark:bg-[#1c1f2b] rounded-xl shadow">
          <h1 className="text-xl font-bold mb-4 text-center">پروفایل کاربری</h1>

          {error && (
            <div className="bg-red-100 text-red-700 p-2 rounded mb-2 text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* نام */}
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm mb-1">نام</label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={handleChange('firstName')}
                  className="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-[#12141c] dark:border-gray-600"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm mb-1">نام خانوادگی</label>
                <input
                  type="text"
                  value={form.lastName}
                  onChange={handleChange('lastName')}
                  className="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-[#12141c] dark:border-gray-600"
                />
              </div>
            </div>

            {/* تاریخ تولد شمسی - DatePicker */}
            <div>
              <label className="block text-sm mb-1">تاریخ تولد</label>
              <DatePicker
                value={birthDatePickerValue}
                onChange={handleBirthDateChange}
                calendar={persian}
                locale={persian_fa}
                calendarPosition="bottom-right"
                inputClass="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-[#12141c] dark:border-gray-600"
                format="YYYY-MM-DD"
                placeholder="مثال: 1400-05-21"
              />
            </div>

            {/* استان */}
            <div>
              <label className="block text-sm mb-1">استان</label>
              <select
                value={selectedProvinceId || ''}
                onChange={handleProvinceChange}
                className="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-[#12141c] dark:border-gray-600"
              >
                <option value="">انتخاب کنید...</option>
                {provinces.map((p) => (
                  <option key={p.Id} value={p.Id}>
                    {p.Name}
                  </option>
                ))}
              </select>
            </div>

            {/* شهر */}
            <div>
              <label className="block text-sm mb-1">شهر</label>
              <select
                value={form.cityId || ''}
                onChange={handleCityChange}
                className="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-[#12141c] dark:border-gray-600"
                disabled={!selectedProvinceId}
              >
                <option value="">ابتدا استان را انتخاب کنید...</option>
                {cities.map((c) => (
                  <option key={c.Id} value={c.Id}>
                    {c.Name}
                  </option>
                ))}
              </select>
            </div>

            {/* آدرس و کدپستی */}
            <div>
              <label className="block text-sm mb-1">آدرس</label>
              <textarea
                value={form.addressLine}
                onChange={handleChange('addressLine')}
                className="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-[#12141c] dark:border-gray-600"
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm mb-1">کد پستی</label>
              <input
                type="text"
                value={form.postalCode}
                onChange={handleChange('postalCode')}
                className="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-[#12141c] dark:border-gray-600"
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded text-sm"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره پروفایل'}
            </button>
          </form>
                  {/* دکمه خروج */}
<button
  onClick={() => {
    localStorage.removeItem("token");
    localStorage.removeItem("planId");
    localStorage.removeItem("planTitle");
    // هر چیز دیگری که خودت برای سشن ذخیره می‌کنی
    navigate("/login");
  }}
  className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded text-sm"
>
  خروج
</button>
        </div>



        {/* دکمه تغییر تم */}
        <button
          onClick={() => setDark((prev) => !prev)}
          className="fixed top-4 right-4 z-50 px-3 py-2 bg-gray-700 text-white rounded-full shadow-lg"
        >
          {dark ? <FaSun className="text-yellow-400" /> : <FaMoon className="text-blue-500" />}
        </button>

      </div>
    </div>
  );
}
